// Lógica pura de administración y auditoría de la bitácora de cables:
// períodos, cobertura por día, filtros y utilidades. Sin red ni React, para
// poder probarla entera. La pantalla y el PDF consumen esto.
import { esTardio } from 'lib/tardio';
import { worstEstado } from 'lib/estado';

const DIA_MS = 24 * 3600 * 1000;
const SEIS_HORAS = 6 * 3600 * 1000;

const aFecha = (iso) => new Date(`${iso}T12:00:00Z`);
const aIso = (d) => d.toISOString().slice(0, 10);

export const sumarDias = (iso, n) => aIso(new Date(aFecha(iso).getTime() + n * DIA_MS));

export function diasEntre(desde, hasta) {
  const out = [];
  if (!desde || !hasta || desde > hasta) return out;
  for (let d = desde; d <= hasta; d = sumarDias(d, 1)) out.push(d);
  return out;
}

// Hora de Costa Rica (UTC-6 todo el año, mismo criterio que fechaCR.js).
export function fechaHoraCR(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  return new Date(t - SEIS_HORAS).toISOString().slice(0, 16).replace('T', ' ');
}

// Atajos de período. Devuelve { desde, hasta } en 'YYYY-MM-DD' o null.
export function periodoAtajo(atajo, hoy) {
  const [y, m] = hoy.split('-').map(Number);
  switch (atajo) {
    case '7': case '30': case '90':
      return { desde: sumarDias(hoy, -(Number(atajo) - 1)), hasta: hoy };
    case 'anio':
      return { desde: `${y}-01-01`, hasta: hoy };
    case 'mes_anterior': {
      const inicio = new Date(Date.UTC(m === 1 ? y - 1 : y, m === 1 ? 11 : m - 2, 1, 12));
      const fin = new Date(Date.UTC(y, m - 1, 1, 12) - DIA_MS);
      return { desde: aIso(inicio), hasta: aIso(fin) };
    }
    case '12m':
      return { desde: sumarDias(`${y - 1}-${hoy.slice(5)}`, 1), hasta: hoy };
    default:
      return null;
  }
}

export const vigentes = (rows) => (rows || []).filter((r) => r && !r.anulada);

export const normalizarTexto = (s) =>
  String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export function filtrarInspecciones(rows, { guia = '', estado = '' } = {}) {
  const g = normalizarTexto(guia);
  return (rows || []).filter((r) =>
    (!g || normalizarTexto(r.guia_nombre).includes(g)) && (!estado || r.estado_general === estado));
}

// Un registro por día del período (hasta hoy; los días futuros no cuentan).
// estado: inspeccionado | sin_operacion | sin_registro | pendiente (hoy, aún sin inspección).
export function coberturaPorDia({ inspecciones, diasSinOperacion, desde, hasta, hoy }) {
  const tope = hasta < hoy ? hasta : hoy;
  const porFecha = new Map();
  for (const r of inspecciones || []) {
    if (!porFecha.has(r.fecha)) porFecha.set(r.fecha, []);
    porFecha.get(r.fecha).push(r);
  }
  const sinOp = new Map();
  const sinOpAnuladas = new Map();
  for (const s of diasSinOperacion || []) {
    if (!s) continue;
    if (!s.anulada) sinOp.set(s.fecha, s);
    else {
      if (!sinOpAnuladas.has(s.fecha)) sinOpAnuladas.set(s.fecha, []);
      sinOpAnuladas.get(s.fecha).push(s);
    }
  }

  return diasEntre(desde, tope).map((fecha) => {
    const todas = porFecha.get(fecha) || [];
    const vig = vigentes(todas);
    const anuladas = todas.filter((r) => r.anulada);
    const marca = sinOp.get(fecha) || null;
    let estado;
    if (vig.length) estado = 'inspeccionado';
    else if (marca) estado = 'sin_operacion';
    else if (fecha === hoy) estado = 'pendiente';
    else estado = 'sin_registro';
    const resultado = vig.length
      ? worstEstado(Object.fromEntries(vig.map((r, i) => [i, r.estado_general])))
      : null;
    return {
      fecha,
      estado,
      resultado,
      inspecciones: vig,
      anuladas,
      inspectores: [...new Set(vig.map((r) => (r.guia_nombre || '').trim()).filter(Boolean))],
      tardio: vig.some(esTardio),
      sinOperacion: marca,
      // Marcas de "sin operación" que se pusieron y se anularon: no cuentan,
      // pero el paquete de auditoría las muestra (no se pierde el rastro).
      marcasAnuladas: sinOpAnuladas.get(fecha) || [],
    };
  });
}

export function resumenCobertura(dias) {
  const r = { dias: 0, conInspeccion: 0, sinOperacion: 0, sinRegistro: 0, pendienteHoy: 0,
    inspecciones: 0, tardios: 0, anuladas: 0, conFalla: 0, conAlerta: 0 };
  for (const d of dias || []) {
    r.dias++;
    if (d.estado === 'inspeccionado') r.conInspeccion++;
    else if (d.estado === 'sin_operacion') r.sinOperacion++;
    else if (d.estado === 'pendiente') r.pendienteHoy++;
    else r.sinRegistro++;
    r.inspecciones += d.inspecciones.length;
    r.tardios += d.inspecciones.filter(esTardio).length;
    r.anuladas += d.anuladas.length;
    r.conFalla += d.inspecciones.filter((x) => x.estado_general === 'falla').length;
    r.conAlerta += d.inspecciones.filter((x) => x.estado_general === 'alerta').length;
  }
  return r;
}

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// Frase honesta para el PDF y la pantalla: nunca "todas aprobadas" sin contar.
// completo = false cuando NO se pudo consultar los días sin operación: entonces
// no se cuentan días sin inspección (un día sin operación saldría como hueco) y
// la frase lo dice.
export const AVISO_SIN_OPERACION = 'No se pudo consultar los días que no se operó: los días sin inspección no se calcularon.';
export function textoResumenPeriodo({ inspecciones = 0, sinRegistro = 0, sinOperacion = 0, completo = true }) {
  if (!completo) {
    return `${plural(inspecciones, 'inspección en el período', 'inspecciones en el período')}. ${AVISO_SIN_OPERACION}`;
  }
  const huecos = plural(sinRegistro, 'día sin inspección', 'días sin inspección');
  if (!inspecciones) return `No hubo inspecciones registradas en el período (${huecos}).`;
  const partes = [plural(inspecciones, 'inspección en el período', 'inspecciones en el período'), huecos];
  if (sinOperacion) partes.push(plural(sinOperacion, 'día que no se operó', 'días que no se operó'));
  return `${partes.join(', ')}.`;
}

export const motivoValido = (m) => String(m || '').trim().length >= 5;

export const formatoFolio = (folio) =>
  folio === null || folio === undefined || folio === '' ? '' : `CAB-${String(folio).padStart(5, '0')}`;

// Ejecuta fn sobre items con como máximo `limite` promesas a la vez; respeta el orden.
export async function mapConLimite(items, limite, fn) {
  const lista = [...(items || [])];
  const out = new Array(lista.length);
  let siguiente = 0;
  const trabajador = async () => {
    while (siguiente < lista.length) {
      const i = siguiente++;
      out[i] = await fn(lista[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limite, lista.length)) }, trabajador));
  return out;
}

export { plural };

// ── Fechas en pantalla ──
// Un solo formato legible en toda la parte administrativa: "mié 7 oct 2026".
// El ISO (2026-10-07) queda solo para exportaciones (CSV, Excel, PDF, JSON).
// Nombres fijos y no toLocaleDateString: el ICU de cada navegador abrevia
// distinto ("sept." / "set."), y en Costa Rica se dice "setiembre".
const DIAS_CORTOS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];

export function fechaCorta(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return '';
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  return `${DIAS_CORTOS[d.getUTCDay()]} ${d.getUTCDate()} ${MESES_CORTOS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

// Marca de tiempo (timestamptz) en hora de Costa Rica: "mié 7 oct 2026, 14:05".
export function fechaHoraCorta(ts) {
  const cr = fechaHoraCR(ts);
  if (!cr) return '';
  return `${fechaCorta(cr.slice(0, 10))}, ${cr.slice(11)}`;
}

// Solo la hora de Costa Rica, "14:05".
export const horaCR = (ts) => fechaHoraCR(ts).slice(11);

// ── Quién ──
// Cuenta del kiosko del parque (misma que KIOSK_EMAIL en AuthContext). Una
// inspección se identifica como "kiosco" si quien la registró (created_by)
// resuelve, por cables_nombres, a esta cuenta.
export const CUENTA_KIOSCO = 'kiosko@vlscr.com';

// UUID corto para cuando no se pudo resolver el nombre (la RPC falta o falló).
export const uuidCorto = (id) => (id ? `${String(id).slice(0, 8)}…` : '');

// Nombre de un usuario de Auth: el que dio cables_nombres, o el UUID corto.
export const nombreUsuario = (id, nombres = {}) => (id ? nombres[id] || uuidCorto(id) : 'Sin dato');

export const METODO_LABEL = {
  pin: 'PIN',
  kiosco: 'Kiosco',
  sso: 'Usuario (SSO)',
  importada: 'Importada de MaintainX',
  declarada: 'Declarada (registrada después)',
};

// Cómo se identificó el inspector. PIN (hay sesión de PIN), importada
// (MaintainX), kiosco (la cuenta del kiosko), SSO (el resto).
export function metodoIdentificacion(r, nombres = {}) {
  if (!r) return 'sso';
  if (r.pin_sesion_id) return 'pin';
  if (r.origen === 'maintainx') return 'importada';
  if (r.origen === 'declarada') return 'declarada';
  if (r.created_by && String(nombres[r.created_by] || '').trim().toLowerCase() === CUENTA_KIOSCO) return 'kiosco';
  return 'sso';
}

// Ids de usuario que aparecen en filas (inspecciones, marcas, reportes) para
// pedir sus nombres en una sola llamada.
export function idsUsuarios(...listas) {
  const out = new Set();
  for (const lista of listas) {
    for (const f of lista || []) {
      for (const k of ['created_by', 'anulada_por', 'registrado_por', 'generado_por']) {
        if (f?.[k]) out.add(f[k]);
      }
    }
  }
  return [...out];
}
